// components/commands/BaguaCameraPanel.jsx
import React, { useState, useRef, useEffect } from "react";
import AWS from "aws-sdk";
import { saveCameraResponseImage } from "../../GeoVisionAI";
import BaguaSVG from "../BaguaSVG";
import useCompassHeading from "../../hooks/useCompassHeading";

async function waitForSharpFrame(video, settleFrames = 2) {
  if (video.readyState < 1) {
    await new Promise((res) => (video.onloadedmetadata = res));
  }
  // Give the camera a couple of real frames to settle
  await new Promise((res) => {
    let count = 0;
    const step = () => {
      if ("requestVideoFrameCallback" in video) {
        video.requestVideoFrameCallback(() => {
          if (++count >= settleFrames) res();
          else step();
        });
      } else {
        // Fallback: ~120ms is enough on most devices
        setTimeout(() => (++count >= settleFrames ? res() : step()), 60);
      }
    };
    step();
  });
}

export default function BaguaCameraPanel({ projectId, onSubmit }) {
  const image_map = useRef(new Map());
  const context = useRef(new Map());

  const initialImages = {
    N: null,
    NE: null,
    E: null,
    SE: null,
    S: null,
    SW: null,
    W: null,
    NW: null,
  };

  const [images, setImages] = useState(initialImages);
  const [activeRegion, setActiveRegion] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const { heading, supported, permission } = useCompassHeading();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Rotation so N in the SVG points to real North
  const cameraFlip = 0;
  const correctedHeading = (heading + cameraFlip) % 360;
  const baguaRotation = correctedHeading == null ? 0 : -correctedHeading; // negative to counter-rotate UI

  useEffect(() => {
    const creds = AWS.config.credentials;
    if (creds && typeof creds.get === "function") {
      creds.get((err) => {
        if (!err) window.userIdGlobal = creds.identityId;
      });
    } else if (creds?.identityId) {
      window.userIdGlobal = creds.identityId;
    }
    window.projectIdGlobal = projectId;
  }, [projectId]);

  const resetAll = () => {
    setImages(initialImages);
    image_map.current.clear();
    setSubmitted(false);
    console.log("All images and image_map have been reset");
  };

  const directionOrder = ["SE", "S", "SW", "E", null, "W", "NE", "N", "NW"];

  const handleRegionClick = async (region) => {
    if (region === "center") {
      const contextObj = Object.fromEntries(context.current); // {N:key, ...}
      console.log("handleRegionClick", contextObj);
      onSubmit?.({ context: contextObj });
      setSubmitted(true);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      await waitForSharpFrame(video);

      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL("image/png");

      // stop cam
      stream.getTracks().forEach((t) => t.stop());

      const compassHeading =
        heading == null
          ? (DEFAULT_DEGREES[region] ?? "na") // e.g., "center" → "na"
          : heading.toFixed(0);

      const { bucket, key } = await saveCameraResponseImage(
        window.userIdGlobal,
        window.projectIdGlobal,
        dataUrl,
        AWS.config.credentials,
        compassHeading,
      );

      context.current.set(region, key);

      console.log("Context map: ", context);

      setImages((prev) => ({ ...prev, [region]: dataUrl }));
      image_map.current.set(region, dataUrl);
      console.log(`Captured & stored image for region "${region}"`);
    } catch (err) {
      console.error(err);
      alert("Unable to capture image.");
    }
  };
  const DEFAULT_DEGREES = {
    N: 0,
    NE: 45,
    E: 90,
    SE: 135,
    S: 180,
    SW: 225,
    W: 270,
    NW: 315,
  };

  useEffect(() => {
    let stream;
    async function startCamera() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      } catch {
        alert("Unable to access camera.");
        setActiveRegion(null);
      }
    }
    if (activeRegion) startCamera();
    return () => stream && stream.getTracks().forEach((t) => t.stop());
  }, [activeRegion]);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/png");
    setImages((prev) => ({ ...prev, [activeRegion]: dataUrl }));
    setActiveRegion(null);
  };

  return (
    <div className="p-4">
      {!supported && (
        <p className="text-sm text-red-600">
          Compass not supported on this device/browser.
        </p>
      )}
      {permission === "denied" && (
        <p className="text-sm text-red-600">
          Compass permission denied. Tap the lock icon in the address bar and
          enable Motion/Orientation.
        </p>
      )}
      {heading != null && (
        <p className="text-xs opacity-70">Heading: {heading.toFixed(0)}°</p>
      )}

      {/* Rotate Bagua so its N is world-aligned */}
      <div
        style={{
          transform: `rotate(${baguaRotation}deg)`,
          transformOrigin: "50% 50%",
        }}
      >
        <BaguaSVG
          images={images}
          submitted={submitted}
          onRegionClick={handleRegionClick}
          onSave={() => {
            const contextObj = Object.fromEntries(context.current); // {N:key, ...}
            console.log("[Bagua] submit context:", contextObj);
            onSubmit?.({ images, context: contextObj });
          }}
        />
      </div>

      <div className="flex items-center mb-4">
        <button
          onClick={resetAll}
          className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-2xl shadow-lg transform transition duration-200 hover:scale-105"
        >
          <span className="mr-3 text-2xl">🧹</span>
          Reset All
        </button>
      </div>

      <div className="mt-4 mx-auto" style={{ width: 200 }}>
        <table className="mt-4 mx-auto border-collapse border border-gray-300">
          <tbody>
            {[0, 1, 2].map((row) => (
              <tr key={row}>
                {[0, 1, 2].map((col) => {
                  const idx = row * 3 + col;
                  const region = directionOrder[idx];
                  const src = region ? images[region] : null;
                  return (
                    <td
                      key={col}
                      className="border border-gray-300 w-4 h-4 p-0"
                    >
                      {region && src && (
                        <img
                          src={src}
                          alt={region}
                          className="h-full w-full object-cover rounded"
                          style={{ width: 200 }}
                        />
                      )}
                      {region === null && (
                        <button
                          onClick={() => {
                            const contextObj = Object.fromEntries(
                              context.current,
                            ); // {N: key, NE: key, ...}
                            console.log("[Bagua] submit context:", contextObj);
                            onSubmit?.({ context: contextObj });
                          }}
                          className="w-full h-full flex items-center justify-center font-bold"
                        >
                          Save
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <video ref={videoRef} style={{ display: "none" }} playsInline muted />
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
